import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react'

import AppDenyListCard from './appDenyList/AppDenyListCard'
import BlockedUrlsCard from './blockedUrls/BlockedUrlsCard'
import ConsentChatMessageSection from './components/ConsentChatMessageSection'
import DataControlsSection from './components/DataControlsSection'
import DefaultSeatTypeSection from './components/DefaultSeatTypeSection'
import DomainCaptureSection from './components/DomainCapture'
import MdmRolloutSection from './components/MdmRollout'
import NoteSharingAudienceSection from './components/NoteSharingAudienceSection'
import NoteSharingContentSection from './components/NoteSharingContentSection'
import NotetakerControlsSection from './components/NotetakerControlsSection'
import OrgInsightsSection from './components/OrgInsightsSection'
import IpAllowlistCard from './ipAllowlist/IpAllowlistCard'
import NotetakerBlockedDomainsCard from './notetakerBlockedDomains/NotetakerBlockedDomainsCard'
import ConnectorAccessSection from './ConnectorAccessSection'
import {
  getAllowCloudSync,
  getEffectiveNotetakerRetention,
  isFiniteRetentionWindow,
  localDataPolicyConfirm,
  retentionRequiresConfirm,
} from './dataControls'
import HipaaBaa from './HipaaBaa'
import PermissionTooltip from './PermissionTooltip'
import { trackedSettingValue } from './settingAnalytics'
import SettingItem from './SettingItem'
import SSOSection from './SSOSection'
import {
  DEFAULT_SETTINGS,
  normalizeLocalDataPolicy,
  resolveMdmProviders,
  resolveMdmUpdateIntent,
  resolveNoteSharingAudience,
  resolveNoteSharingContent,
  resolveOrgInsightsMode,
  SETTINGS_CONFIG,
} from './utils'

import styles from './styles.module.scss'

import { revokeEnterpriseHipaaBaa } from '@/api/enterprise'
import InfoIcon from '@/assets/icons/info.svg?react'
import OpenLinkIcon from '@/assets/icons/openLink.svg?react'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Input from '@/components/Input'
import { LogoUpload } from '@/components/LogoUpload'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { useHipaaGatingEnabled } from '@/hooks/useHipaaGatingEnabled'
import { useSeatSelectionEnabled } from '@/hooks/useSeatManagementEnabled'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { AppDispatch } from '@/store'
import {
  getEnterpriseThunk,
  selectCurrentEnterprise,
  selectHasActiveOrTrialingEnterpriseSubscription,
  selectIsAdmin,
  selectIsLoading,
  selectPlanType,
  updateEnterpriseSettingsThunk,
  updateEnterpriseThunk,
  updateNotetakerRetentionThunk,
} from '@/store/features/user/userSlice'
import {
  ContextAwarenessPolicy,
  Enterprise,
  type EnterpriseSettings,
  LocalDataPolicy,
  MdmProvider,
  MdmUpdateIntent,
  NoteSharingAudience,
  NoteSharingContent,
  NotetakerTranscriptRetention,
  OrgInsightsMode,
  PlanType,
  type ProductSeatType,
  SettingType,
  SnippetSharingPolicy,
} from '@/types'
import { CONNECTORS_ACCESS_FLAG, resolveConnectorAccess } from '@/utils/connectorAccess'
import { EventName } from '@/utils/eventDefinitions'

/** The consent notice is admin-authored free text up to 450 characters. Every other string
 *  setting here is a bounded enum, so the raw value is safe; this one is not, and the analytics
 *  payload is not the place for it. Report which state was chosen instead. */
const LOCAL_DATA_POLICY_OPTIONS: Array<Omit<DropdownItem, 'selected'> & { id: LocalDataPolicy }> = [
  {
    id: LocalDataPolicy.StoreNormally,
    title: 'Managed individually',
    helperText: 'Users can change this setting in their Wispr Flow app',
  },
  {
    id: LocalDataPolicy.AlwaysStore,
    title: 'Always store locally',
    helperText: "Transcripts always stay on each member's device, and members can't change this",
  },
  {
    id: LocalDataPolicy.DeleteAfter24h,
    title: 'Auto-delete local data every 24 hours',
    helperText: 'Transcripts are automatically deleted after 24 hours',
  },
  {
    id: LocalDataPolicy.NeverStore,
    title: 'Never store data locally',
    helperText: 'Transcripts are never saved to the device',
  },
]

const getLocalDataPolicyItems = (current: LocalDataPolicy, showAlwaysStore: boolean): DropdownItem[] =>
  LOCAL_DATA_POLICY_OPTIONS.filter((option) => showAlwaysStore || option.id !== LocalDataPolicy.AlwaysStore).map(
    (option) => ({ ...option, selected: option.id === current })
  )

type LocalDataStorageConfirmCopy = { title: string; message: string; confirmLabel: string }

const LOCAL_DATA_STORAGE_CONFIRM_COPY: Record<
  'tighten24h' | 'tightenNeverStore' | 'unlock',
  LocalDataStorageConfirmCopy
> = {
  tighten24h: {
    title: 'Auto-delete local data every 24 hours?',
    message: "Dictation history older than 24 hours will be deleted from every member's device. This can't be undone.",
    confirmLabel: 'Turn on auto-delete',
  },
  tightenNeverStore: {
    title: 'Stop storing data locally?',
    message: "All dictation history will be deleted from every member's device. This can't be undone.",
    confirmLabel: 'Stop storing locally',
  },
  unlock: {
    title: 'Let members choose local storage?',
    message:
      "Each member's own setting applies again. Members who chose auto-delete or never store will have dictation history deleted from their device. This can't be undone.",
    confirmLabel: 'Let members choose',
  },
}

const CONTEXT_AWARENESS_OPTIONS = [
  {
    id: ContextAwarenessPolicy.Available,
    title: 'Available',
    helperText: 'Users on the enterprise can enable or disable context awareness.',
  },
  {
    id: ContextAwarenessPolicy.Disabled,
    title: 'Disable for all users',
    helperText: 'All users on the enterprise will have context awareness disabled.',
  },
]

const getContextAwarenessDropdownItems = (currentValue: ContextAwarenessPolicy): DropdownItem[] => {
  return CONTEXT_AWARENESS_OPTIONS.map((option) => ({
    ...option,
    selected: option.id === currentValue,
  }))
}

const SNIPPET_SHARING_POLICY_OPTIONS = [
  {
    id: SnippetSharingPolicy.Unrestricted,
    title: 'Unrestricted',
    helperText: 'Members can share snippets with their department or the entire organization',
  },
  {
    id: SnippetSharingPolicy.DepartmentOnly,
    title: 'Department only',
    helperText: 'Members can only share snippets within their own department',
  },
]

const getSnippetSharingPolicyDropdownItems = (currentValue: SnippetSharingPolicy): DropdownItem[] => {
  return SNIPPET_SHARING_POLICY_OPTIONS.map((option) => ({
    ...option,
    selected: option.id === currentValue,
  }))
}

const HIPAA_TOOLTIP =
  'With HIPAA enabled, Notetaker will be disabled. Note that this blocks access to cross-device features.'
const HIPAA_WITH_NOTETAKER_TOOLTIP = 'With HIPAA enabled, cross-device features are unavailable.'

const CLOUD_DATA_STORAGE_TOOLTIP =
  'How dictation transcripts are stored securely in the cloud. You choose the most permissive option allowed. Members can pick a stricter option in their Wispr Flow app, but never a looser one.'

const LOCAL_DATA_STORAGE_TOOLTIP =
  "How dictation transcripts are stored on each member's device. You choose the most permissive option allowed. Members can pick a stricter option in their Wispr Flow app, but never a looser one."

// Only names Always store locally while the option is visible, so the tooltip never points at a hidden choice.
const LOCAL_DATA_STORAGE_LOCKABLE_TOOLTIP =
  "How dictation transcripts are stored on each member's device. You choose the most permissive option allowed, and members can pick a stricter one in their Wispr Flow app. With Always store locally, members can't change it."

const CONTEXT_AWARENESS_TOOLTIP =
  'Lets Flow read on-screen text near the cursor to improve dictation accuracy and formatting. Turning this off disables screen reading for every member.'

const TRANSCRIPT_RETENTION_TOOLTIP =
  'How long meeting transcripts are kept in the cloud before automatic deletion. Managed individually lets each member choose; picking a window enforces it for the whole organization.'

const SCRATCHPAD_TOOLTIP =
  'Turns the Scratchpad feature on or off for your entire organization. Cloud data is kept and restored if you re-enable it.'

// Same write path as the old Private cloud sync control — only the labels moved to the design copy.
const CLOUD_SYNC_MANAGED_ID = 'managed_individually'

const getCloudDataStorageItems = (allowCloudSync: boolean): DropdownItem[] => [
  {
    id: CLOUD_SYNC_MANAGED_ID,
    title: 'Managed individually',
    helperText: 'Users can change this setting in their Wispr Flow app',
    selected: allowCloudSync,
  },
  {
    id: 'never_store_in_cloud',
    title: 'Never store data in cloud',
    helperText: 'Dictation history is never saved to the cloud',
    selected: !allowCloudSync,
  },
]

const noopSetItems = () => undefined

// Cloud Sync off stops new uploads but does not retract a stored window — the reaper
// keeps deleting previously-uploaded transcripts on it.
const RETENTION_CLOUD_SYNC_OFF_TOOLTIP =
  'Cloud data storage is off, so no new transcripts are stored. Transcripts uploaded before it was turned off are still deleted on this schedule.'
const CONSENT_CONFIRMATION_TOOLTIP =
  'Members are blocked from starting a Notetaker meeting until they confirm they’ve obtained consent from participants.'
const CONSENT_CHAT_TOOLTIP =
  'Wispr posts this notice in the meeting chat when a member starts the Notetaker. On, members can’t turn it off or change the text. Off leaves both to each member.'

// Display order only — the confirm gate ranks by RETENTION_SEVERITY in dataControls.ts.
const NOTETAKER_RETENTION_OPTIONS = [
  {
    id: NotetakerTranscriptRetention.ManagedIndividually,
    title: 'Managed individually',
    helperText: 'Each user controls how long their Notetaker transcripts are kept',
  },
  {
    id: NotetakerTranscriptRetention.NeverDelete,
    title: 'Never delete',
    helperText: 'Notetaker transcripts are kept indefinitely for everyone',
  },
  {
    id: NotetakerTranscriptRetention.Days1,
    title: '1 day',
    helperText: 'Transcripts older than 1 day are automatically deleted',
  },
  {
    id: NotetakerTranscriptRetention.Days7,
    title: '7 days',
    helperText: 'Transcripts older than 7 days are automatically deleted',
  },
  {
    id: NotetakerTranscriptRetention.Days30,
    title: '30 days',
    helperText: 'Transcripts older than 30 days are automatically deleted',
  },
  {
    id: NotetakerTranscriptRetention.Days90,
    title: '90 days',
    helperText: 'Transcripts older than 90 days are automatically deleted',
  },
  {
    id: NotetakerTranscriptRetention.Days180,
    title: '180 days',
    helperText: 'Transcripts older than 180 days are automatically deleted',
  },
  {
    id: NotetakerTranscriptRetention.Days365,
    title: '365 days',
    helperText: 'Transcripts older than 365 days are automatically deleted',
  },
]

// Partial: only finite windows can be a destructive target.
const RETENTION_WINDOW_LABEL: Partial<Record<NotetakerTranscriptRetention, string>> = {
  [NotetakerTranscriptRetention.Days1]: '1 day',
  [NotetakerTranscriptRetention.Days7]: '7 days',
  [NotetakerTranscriptRetention.Days30]: '30 days',
  [NotetakerTranscriptRetention.Days90]: '90 days',
  [NotetakerTranscriptRetention.Days180]: '180 days',
  [NotetakerTranscriptRetention.Days365]: '365 days',
}

const getNotetakerRetentionDropdownItems = (effective: NotetakerTranscriptRetention): DropdownItem[] =>
  NOTETAKER_RETENTION_OPTIONS.map((option) => ({
    ...option,
    selected: option.id === effective,
  }))

const SCRATCHPAD_ENABLED_ID = 'enabled'
const SCRATCHPAD_OPTIONS = [
  {
    id: SCRATCHPAD_ENABLED_ID,
    title: 'Enabled for everyone',
    helperText: 'Members can use Scratchpad',
  },
  {
    id: 'disabled',
    title: 'Disabled for everyone',
    helperText: 'Disables Scratchpad for your organization.',
  },
] as const

const getScratchpadDropdownItems = (enabled: boolean): DropdownItem[] =>
  SCRATCHPAD_OPTIONS.map((option) => ({
    ...option,
    selected: enabled === (option.id === SCRATCHPAD_ENABLED_ID),
  }))

const FEATURE_ENABLED_ID = 'enabled'
const FEATURE_DISABLED_ID = 'disabled'

type OrgFeatureCopy = {
  key: SettingType.allowDictionary | SettingType.allowSnippets
  testId: string
  label: string
  tooltip: string
  enabledHelperText: string
  disabledHelperText: string
  confirmTitle: string
  confirmMessage: string
  confirmWarning?: string
  confirmLabel: string
}

const DICTIONARY_FEATURE: OrgFeatureCopy = {
  key: SettingType.allowDictionary,
  testId: 'dictionary-allow-setting',
  label: 'Dictionary',
  tooltip:
    'Turns Dictionary on or off for your entire organization. Saved words are kept and come back if you turn it back on.',
  enabledHelperText: 'Members can add and edit Dictionary words',
  disabledHelperText: 'Hides Dictionary for your organization',
  confirmTitle: 'Turn off Dictionary for your organization?',
  confirmMessage:
    'Members will lose the Dictionary in their Wispr Flow app when it next syncs and can no longer add or edit words. Words they already saved are kept, still apply during dictation, and reappear if you turn Dictionary back on.',
  confirmLabel: 'Turn off Dictionary',
}

const SNIPPETS_FEATURE: OrgFeatureCopy = {
  key: SettingType.allowSnippets,
  testId: 'snippets-allow-setting',
  label: 'Snippets',
  tooltip:
    'Turns Snippets on or off for your entire organization. Saved snippets are kept and come back if you turn it back on.',
  enabledHelperText: 'Members can add and edit Snippets',
  disabledHelperText: 'Hides Snippets for your organization',
  confirmTitle: 'Turn off Snippets for your organization?',
  confirmMessage:
    'Members will lose Snippets in their Wispr Flow app when it next syncs and can no longer add or edit snippets. Snippets they already saved are kept, still expand during dictation, and reappear if you turn Snippets back on.',
  confirmWarning: 'Snippet sharing settings stay as they are but have no effect while Snippets is off.',
  confirmLabel: 'Turn off Snippets',
}

const ORG_FEATURES: readonly OrgFeatureCopy[] = [DICTIONARY_FEATURE, SNIPPETS_FEATURE]

const SNIPPET_SHARING_INERT_TOOLTIP =
  'Snippet sharing has no effect while Snippets is turned off for your organization.'

const getOrgFeatureDropdownItems = (feature: OrgFeatureCopy, enabled: boolean): DropdownItem[] => [
  {
    id: FEATURE_ENABLED_ID,
    title: 'Enabled for everyone',
    helperText: feature.enabledHelperText,
    selected: enabled,
  },
  {
    id: FEATURE_DISABLED_ID,
    title: 'Disabled for everyone',
    helperText: feature.disabledHelperText,
    selected: !enabled,
  },
]

// Items derive from optimisticSettings each render, so Dropdown's setItems is inert.
const noopSetRetentionItems = () => undefined

const HIDE_LOGO_SETTING = true

interface AutoInviteDomainSettingsProps {
  enterprise: Enterprise | null
  optimisticSettings: EnterpriseSettings
  toggleSettings: (key: keyof EnterpriseSettings, checked: boolean) => void
  isAdmin: boolean
}

const AutoInviteDomainSettings = ({
  enterprise,
  optimisticSettings,
  toggleSettings,
  isAdmin,
}: AutoInviteDomainSettingsProps) => {
  const hasDepts = useHasDepartments()
  const [isDomainsExpanded, setIsDomainsExpanded] = useState(false)

  // Use domains list if it has items, otherwise fallback to single domain
  let domains: string[] = []
  // Use enterprise primary domain as the default
  if (enterprise?.domain) {
    domains = [enterprise.domain]
  }
  // If the enterprise's domains list is populated, use it instead of the primary domain
  if (enterprise?.domains?.length) {
    domains = enterprise.domains
  }

  const numDomains = domains.length

  if (numDomains === 0) {
    return (
      <div className={styles.emptyDisclaimer}>
        Your organization is not linked to a domain. Please contact us at{' '}
        <a href="mailto:enterprise@wispr.ai">enterprise@wispr.ai</a> to make changes.
      </div>
    )
  }

  const labelText =
    numDomains === 1
      ? `Automatically add new users with a ${domains[0]} email`
      : 'Automatically add new users from your email domains'

  return (
    <>
      <SettingItem
        config={SETTINGS_CONFIG[SettingType.autoInvite]}
        optimisticSettings={optimisticSettings}
        toggleSettings={toggleSettings}
        isAdmin={isAdmin}
        overrideLabel={labelText}
        labelTooltip={`New users who sign up with this domain will automatically be added to your ${hasDepts ? 'organization' : 'team'} as a new seat and billed at the next billing cycle`}
      />
      <div className={styles.domainsContainer}>
        <Button
          className={styles.domainsToggle}
          type="link"
          title={
            isDomainsExpanded
              ? `Hide ${numDomains} domain${numDomains === 1 ? '' : 's'}`
              : `See ${numDomains} domain${numDomains === 1 ? '' : 's'}`
          }
          onClick={() => setIsDomainsExpanded(!isDomainsExpanded)}
        />
        {isDomainsExpanded && (
          <div className={styles.domainsTableWrapper}>
            <table className={styles.domainsTable}>
              <tbody>
                {domains.map((domain) => (
                  <tr key={domain}>
                    <td>{domain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

type PendingToggle = {
  key: keyof EnterpriseSettings
  checked: boolean
  title: string
  message: React.ReactNode
  warning?: React.ReactNode
  confirmLabel: string
}

type PendingSettingUpdate = {
  requestId: symbol
  value: EnterpriseSettings[keyof EnterpriseSettings]
  settled: boolean
}

type OrgFeatureRowProps = {
  feature: OrgFeatureCopy
  enabled: boolean
  isAdmin: boolean
  onSelect: (selectedItems: DropdownItem[]) => void
}

const OrgFeatureRow = ({ feature, enabled, isAdmin, onSelect }: OrgFeatureRowProps) => {
  const tooltipId = useId()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  return (
    <div className={styles.settingsItem} data-testid={feature.testId}>
      <div className={styles.toggleLabel}>
        {feature.label}
        <Tooltip
          text={
            <span id={tooltipId} role="tooltip">
              {feature.tooltip}
            </span>
          }
          placement="top"
          hideArrow
          open={tooltipOpen}
        >
          <button
            type="button"
            className={styles.infoTooltipButton}
            aria-label={`About ${feature.label}`}
            aria-describedby={tooltipId}
            onFocus={() => setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
          >
            <InfoIcon className={styles.infoIcon} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
      <PermissionTooltip>
        <Dropdown
          items={getOrgFeatureDropdownItems(feature, enabled)}
          setItems={noopSetItems}
          onSelect={onSelect}
          placeholder="Select policy"
          className={styles.localDataPolicyDropdown}
          disabled={!isAdmin}
          ariaLabel={feature.label}
        />
      </PermissionTooltip>
    </div>
  )
}

const EnterpriseSettingsPage = () => {
  const dispatch = useDispatch<AppDispatch>()
  const scratchpadTooltipId = useId()
  const [scratchpadTooltipOpen, setScratchpadTooltipOpen] = useState(false)
  const isSaveLoading = useSelector(selectIsLoading)
  const enterprise = useSelector(selectCurrentEnterprise)
  const planType = useSelector(selectPlanType)
  const hasLiveEnterpriseSubscription = useSelector(selectHasActiveOrTrialingEnterpriseSubscription)
  const isAdmin = useSelector(selectIsAdmin)
  // Cloud data storage is Team-or-Enterprise (any active subscription), unlike the
  // Enterprise-only rows below it.
  const hasActiveSubscription = useSelector(selectHasActiveOrTrialingEnterpriseSubscription)
  // View as Admin shows only what the customer can use, without upgrade prompts.
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null
  const [name, setName] = useState(enterprise?.name || '')
  const [isViewingHipaa, setIsViewingHipaa] = useState(false)
  const [showBaaNotetakerGate, setShowBaaNotetakerGate] = useState(false)
  const [isRevoking, setIsRevoking] = useState(false)
  const [isDisablingHipaaForNotetaker, setIsDisablingHipaaForNotetaker] = useState(false)
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const hipaaGatingEnabled = useHipaaGatingEnabled()
  const seatSelectionEnabled = useSeatSelectionEnabled()
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null)
  // Dedicated rollout for the org-wide Notetaker on/off control.
  const notetakerControlsFlagEnabled = useFeatureFlagEnabled('notetaker-controls-enterprise')
  const alwaysStoreLocallyFlagEnabled = useFeatureFlagEnabled('force-local-data-store-enterprise') === true
  // Per-org rollout: sales enables the consent-confirmation gate on request, not every Notetaker admin.
  const consentConfirmationFlagEnabled = useFeatureFlagEnabled('notetaker-consent-confirmation')
  // Explicit false only — undefined-while-loading must not flash the early-access helper in.
  const notetakerBusinessAllowedFlag = useFeatureFlagEnabled('notetaker-business-allowed')
  // Same "absent while undefined" no-flash-in gate as the note-sharing row below.
  const snippetGovernanceFlagEnabled = useFeatureFlagEnabled('enterprise-snippet-governance')
  const mcpConnectorControlsFlagEnabled = useFeatureFlagEnabled('enterprise-mcp-connector-controls')
  const connectorsAccessFlagEnabled = useFeatureFlagEnabled(CONNECTORS_ACCESS_FLAG)
  const connectorsAccessPayload = useFeatureFlagPayload(CONNECTORS_ACCESS_FLAG)
  const connectorAccess = resolveConnectorAccess(connectorsAccessFlagEnabled, connectorsAccessPayload)
  // Explicit true only: until Notetaker's subprocessors are under the BAA, HIPAA and Notetaker stay exclusive.
  const notetakerHipaaCoexist = useFeatureFlagEnabled('notetaker-hipaa-coexistence') === true
  // Same key the desktop gates org insights on. Absent while undefined, so the row never flashes in.
  const orgInsightsFlagEnabled = useFeatureFlagEnabled('org-insights')
  const consentChatFlagEnabled = useFeatureFlagEnabled('notetaker-consent-chat')
  const transcriptRetentionFlagEnabled = useFeatureFlagEnabled('notetaker-retention')
  const [pendingRetention, setPendingRetention] = useState<NotetakerTranscriptRetention | null>(null)
  // Separate from isSaveLoading — updateNotetakerRetentionThunk has no pending reducer.
  const [isRetentionSaving, setIsRetentionSaving] = useState(false)
  // Copy is frozen at selection: the optimistic write flips the current option while the dialog spins.
  const [pendingLocalDataStorage, setPendingLocalDataStorage] = useState<{
    policy: LocalDataPolicy
    copy: LocalDataStorageConfirmCopy
  } | null>(null)
  const [isSavingLocalDataStorage, setIsSavingLocalDataStorage] = useState(false)

  const [optimisticSettings, setOptimisticSettings] = useState<EnterpriseSettings>({
    ...DEFAULT_SETTINGS,
    ...enterprise?.settings,
    [SettingType.allowScratchpad]: enterprise?.settings?.[SettingType.allowScratchpad] !== false,
    [SettingType.allowDictionary]: enterprise?.settings?.[SettingType.allowDictionary] !== false,
    [SettingType.allowSnippets]: enterprise?.settings?.[SettingType.allowSnippets] !== false,
    [SettingType.notetakerConsentConfirmation]:
      enterprise?.settings?.[SettingType.notetakerConsentConfirmation] === true,
  })
  const optimisticSettingsRef = useRef(optimisticSettings)
  const pendingSettingUpdatesRef = useRef(new Map<keyof EnterpriseSettings, PendingSettingUpdate>())
  const activeSettingsRequestsRef = useRef(0)

  const [contextAwarenessItems, setContextAwarenessItems] = useState<DropdownItem[]>(() =>
    getContextAwarenessDropdownItems(
      (optimisticSettings[SettingType.contextAwareness] as ContextAwarenessPolicy) ?? ContextAwarenessPolicy.Available
    )
  )

  const [pendingSharingBlock, setPendingSharingBlock] = useState(false)
  // Scoped to this save; isSaveLoading is slice-wide and would gate the row on any settings write.
  const [isSavingSharingAudience, setIsSavingSharingAudience] = useState(false)
  const [isSavingSharingContent, setIsSavingSharingContent] = useState(false)
  // Both note-sharing controls write the same settings_json blob, and the backend read-modify-writes
  // it without a row lock — overlapping saves silently lose one. Gate both on either being in flight.
  const isSavingNoteSharing = isSavingSharingAudience || isSavingSharingContent
  const [isSavingOrgInsights, setIsSavingOrgInsights] = useState(false)
  const [isSavingDefaultSeatType, setIsSavingDefaultSeatType] = useState(false)

  const [snippetSharingPolicyItems, setSnippetSharingPolicyItems] = useState<DropdownItem[]>(() =>
    getSnippetSharingPolicyDropdownItems(
      optimisticSettings[SettingType.snippetSharingPolicy] ?? SnippetSharingPolicy.Unrestricted
    )
  )
  const appDenyAuthoringEnabled =
    planType === PlanType.Enterprise && optimisticSettings[SettingType.appDenyListUiVisible] === true
  const showAppDenySection =
    appDenyAuthoringEnabled ||
    (optimisticSettings[SettingType.appDenyList]?.length ?? 0) > 0 ||
    (optimisticSettings[SettingType.browserUrlDenyList]?.length ?? 0) > 0
  // Unset means the backend default (on); only an explicit false disables authoring.
  const notetakerBlockedDomainsAuthoringEnabled =
    planType === PlanType.Enterprise && optimisticSettings[SettingType.notetakerBlockedDomainsUiVisible] !== false
  // Still rendered once rules exist even with the gate off, so an org that loses the
  // gate can wind its own rules down — matching the backend's removal allowance.
  const showNotetakerBlockedDomains =
    notetakerBlockedDomainsAuthoringEnabled ||
    (optimisticSettings[SettingType.notetakerBlockedDomains]?.length ?? 0) > 0

  useEffect(() => {
    const nextSettings: EnterpriseSettings = {
      ...DEFAULT_SETTINGS,
      ...enterprise?.settings,
      [SettingType.allowScratchpad]: enterprise?.settings?.[SettingType.allowScratchpad] !== false,
      [SettingType.allowDictionary]: enterprise?.settings?.[SettingType.allowDictionary] !== false,
      [SettingType.allowSnippets]: enterprise?.settings?.[SettingType.allowSnippets] !== false,
      [SettingType.notetakerConsentConfirmation]:
        enterprise?.settings?.[SettingType.notetakerConsentConfirmation] === true,
    }
    const canReconcileSettledUpdates = activeSettingsRequestsRef.current === 0
    pendingSettingUpdatesRef.current.forEach((update, key) => {
      if (canReconcileSettledUpdates && update.settled && nextSettings[key] === update.value) {
        pendingSettingUpdatesRef.current.delete(key)
      } else {
        nextSettings[key] = update.value as never
      }
    })
    optimisticSettingsRef.current = nextSettings
    setOptimisticSettings(nextSettings)
    setContextAwarenessItems(
      getContextAwarenessDropdownItems(
        (enterprise?.settings?.[SettingType.contextAwareness] as ContextAwarenessPolicy) ??
          ContextAwarenessPolicy.Available
      )
    )
    setSnippetSharingPolicyItems(
      getSnippetSharingPolicyDropdownItems(
        enterprise?.settings?.[SettingType.snippetSharingPolicy] ?? SnippetSharingPolicy.Unrestricted
      )
    )
  }, [enterprise?.settings])

  const handleRevokeBaa = async () => {
    try {
      setIsRevoking(true)
      const { data } = await revokeEnterpriseHipaaBaa()
      await dispatch(getEnterpriseThunk({ needSubscription: false }))
      updateToast(data.message, 'success')
    } catch (error) {
      updateToast(error instanceof Error ? error.message : String(error), 'error')
    } finally {
      setIsRevoking(false)
    }
  }

  // Reverse of handleAcceptBaaClick's gate: enabling Notetaker while HIPAA is signed
  // revokes the BAA and enables Notetaker together, once the admin confirms.
  const handleDisableHipaaForNotetaker = async () => {
    try {
      setIsDisablingHipaaForNotetaker(true)
      const { data } = await revokeEnterpriseHipaaBaa()
      await dispatch(getEnterpriseThunk({ needSubscription: false }))
      await applySettingsPatch({ [SettingType.allowNotetaker]: true })
      updateToast(data.message, 'success')
    } catch (error) {
      updateToast(error instanceof Error ? error.message : String(error), 'error')
    } finally {
      setIsDisablingHipaaForNotetaker(false)
    }
  }

  const didChange = name !== enterprise?.name
  const save = async () => {
    try {
      await dispatch(updateEnterpriseThunk({ name })).unwrap()
      trackEvent?.(EventName.SettingChanged, { setting: 'team_name', value: name, success: true })
      updateToast('Updated organization details', 'success')
    } catch {
      updateToast('Failed to update organization details', 'error')
    }
  }

  const handleLogoUpload = async (base64Logo: string) => {
    try {
      await dispatch(updateEnterpriseThunk({ logo: base64Logo })).unwrap()
      updateToast('Updated organization logo', 'success')
    } catch {
      updateToast("Couldn't upload logo", 'error')
    }
  }

  const patchSettings = async (patch: Partial<EnterpriseSettings>): Promise<boolean> => {
    if (!enterprise) return false

    const requestId = Symbol()
    const keys = Object.keys(patch) as Array<keyof EnterpriseSettings>
    const previousSettings: Partial<EnterpriseSettings> = {}
    for (const key of keys) {
      previousSettings[key] = optimisticSettingsRef.current[key] as never
      pendingSettingUpdatesRef.current.set(key, {
        requestId,
        value: patch[key],
        settled: false,
      })
    }
    activeSettingsRequestsRef.current += 1
    optimisticSettingsRef.current = {
      ...optimisticSettingsRef.current,
      ...patch,
    }
    // allowNotetaker is PATCH write intent only; UI reads notetakerDisabledAt.
    const withNotetakerIntent = (settings: EnterpriseSettings): EnterpriseSettings => {
      if (!(SettingType.allowNotetaker in patch)) {
        return settings
      }
      const next = { ...settings }
      delete next[SettingType.allowNotetaker]
      if (patch[SettingType.allowNotetaker] === false) {
        next.notetakerDisabledAt = next.notetakerDisabledAt ?? new Date().toISOString()
      } else if (patch[SettingType.allowNotetaker] === true) {
        delete next.notetakerDisabledAt
      }
      return next
    }
    optimisticSettingsRef.current = withNotetakerIntent(optimisticSettingsRef.current)
    setOptimisticSettings((current) => withNotetakerIntent({ ...current, ...patch }))

    try {
      await dispatch(
        updateEnterpriseSettingsThunk({
          settings: patch,
        })
      ).unwrap()

      const changedKey = Object.keys(patch)[0] as keyof EnterpriseSettings | undefined
      const trackedValue = changedKey ? trackedSettingValue(changedKey, patch[changedKey]) : undefined
      if (changedKey && trackedValue !== undefined) {
        trackEvent?.(EventName.SettingChanged, {
          setting: changedKey,
          value: trackedValue,
          success: true,
        })
      }

      if (SettingType.autoInvite in patch) {
        trackEvent?.(EventName.AutoInviteToggled, { enabled: Boolean(patch[SettingType.autoInvite]) })
      }
      for (const key of keys) {
        const pendingUpdate = pendingSettingUpdatesRef.current.get(key)
        if (pendingUpdate?.requestId === requestId) {
          pendingUpdate.settled = true
        }
      }

      if (SettingType.allowNotetaker in patch) {
        trackEvent?.(EventName.OrgNotetakerEnabledChanged, {
          enabled: Boolean(patch[SettingType.allowNotetaker]),
        })
      }
      return true
    } catch (error) {
      setOptimisticSettings((current) => {
        const restored = { ...current }
        for (const key of keys) {
          if (pendingSettingUpdatesRef.current.get(key)?.requestId === requestId) {
            restored[key] = previousSettings[key] as never
            pendingSettingUpdatesRef.current.delete(key)
          }
        }
        optimisticSettingsRef.current = restored
        return restored
      })
      updateToast(error as string, 'error')
      return false
    } finally {
      activeSettingsRequestsRef.current -= 1
    }
  }

  const applySettingsPatch = async (patch: Partial<EnterpriseSettings>) => {
    await patchSettings(patch)
  }

  const handleMdmDeclarationSave = (providers: MdmProvider[], updateIntent: MdmUpdateIntent) =>
    patchSettings({ [SettingType.mdmProviders]: providers, [SettingType.mdmUpdateIntent]: updateIntent })

  const applyToggle = async (key: keyof EnterpriseSettings, checked: boolean) => {
    await applySettingsPatch({ [key]: checked })
  }

  const toggleSettings = async (key: keyof EnterpriseSettings, checked: boolean) => {
    if (key === SettingType.allowScratchpad && !checked) {
      setPendingToggle({
        key,
        checked,
        title: 'Turn off Scratchpad for your organization?',
        message:
          'Scratchpad will close for members when their apps sync. MCP access to Scratchpad notes will also be disabled. Unsaved text may be lost. Saved notes will remain on their devices.',
        confirmLabel: 'Turn off Scratchpad',
      })
      return
    }
    const orgFeature = ORG_FEATURES.find((feature) => feature.key === key)
    if (orgFeature && !checked) {
      setPendingToggle({
        key,
        checked,
        title: orgFeature.confirmTitle,
        message: orgFeature.confirmMessage,
        warning: orgFeature.confirmWarning,
        confirmLabel: orgFeature.confirmLabel,
      })
      return
    }
    if (key === SettingType.disableTeamTrial && checked) {
      setPendingToggle({
        key,
        checked,
        title: 'Disable free trial for new members?',
        message:
          'New members joining this organization will not receive a free trial. Existing members currently on a trial will have their trial removed.',
        warning:
          'There is no graceful way to restore an active trial for users whose trial is removed. Re-enabling this setting later only affects future joiners.',
        confirmLabel: 'Disable trial',
      })
      return
    }
    if (key === SettingType.allowTransforms && !checked) {
      setPendingToggle({
        key,
        checked,
        title: 'Turn off Transforms for your organization?',
        message:
          'Transforms is a feature that uses generative AI to refine text. Saved settings and content will not be deleted.',
        confirmLabel: 'Turn off Transforms',
      })
      return
    }
    await applyToggle(key, checked)
  }

  const localDataPolicy = normalizeLocalDataPolicy(optimisticSettings[SettingType.localDataDelete])

  const applyLocalDataPolicy = async (policy: LocalDataPolicy) => {
    setIsSavingLocalDataStorage(true)
    try {
      await applySettingsPatch({ [SettingType.localDataDelete]: policy })
    } finally {
      setIsSavingLocalDataStorage(false)
    }
  }

  const handleLocalDataStorageSelect = async (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0 || isSavingLocalDataStorage) return
    const policy = selectedItems[0].id as LocalDataPolicy
    if (policy === localDataPolicy) return
    const confirm = localDataPolicyConfirm(localDataPolicy, policy)
    if (confirm) {
      const copy =
        confirm === 'unlock'
          ? LOCAL_DATA_STORAGE_CONFIRM_COPY.unlock
          : policy === LocalDataPolicy.NeverStore
            ? LOCAL_DATA_STORAGE_CONFIRM_COPY.tightenNeverStore
            : LOCAL_DATA_STORAGE_CONFIRM_COPY.tighten24h
      setPendingLocalDataStorage({ policy, copy })
      return
    }
    await applyLocalDataPolicy(policy)
  }

  const handleContextAwarenessSelect = async (selectedItems: DropdownItem[]) => {
    if (!enterprise || selectedItems.length === 0) return

    const previousSettings = optimisticSettings
    const previousContextAwarenessItems = contextAwarenessItems
    const value = selectedItems[0].id as ContextAwarenessPolicy
    const newSettings: EnterpriseSettings = {
      ...optimisticSettings,
      [SettingType.contextAwareness]: value,
    }
    setOptimisticSettings(newSettings)

    try {
      await dispatch(
        updateEnterpriseSettingsThunk({
          settings: { [SettingType.contextAwareness]: value },
        })
      ).unwrap()

      trackEvent?.(EventName.SettingChanged, { setting: SettingType.contextAwareness, value, success: true })
    } catch (error) {
      setOptimisticSettings(previousSettings)
      setContextAwarenessItems(previousContextAwarenessItems)
      updateToast(error as string, 'error')
    }
  }

  const noteSharingAudience = resolveNoteSharingAudience(optimisticSettings)

  const applyNoteSharingAudience = async (audience: NoteSharingAudience) => {
    if (!enterprise) return

    const previousSettings = optimisticSettings
    setIsSavingSharingAudience(true)
    // The backend derives the legacy allowNoteSharing bool from the audience — true for the
    // two tiers that permit external sharing. Mirror it locally so the optimistic blob
    // matches what the refetch will return.
    setOptimisticSettings({
      ...optimisticSettings,
      [SettingType.noteSharingAudience]: audience,
      [SettingType.allowNoteSharing]:
        audience === NoteSharingAudience.Public || audience === NoteSharingAudience.External,
    })

    try {
      await dispatch(
        updateEnterpriseSettingsThunk({
          settings: { [SettingType.noteSharingAudience]: audience },
        })
      ).unwrap()

      trackEvent?.(EventName.SettingChanged, {
        setting: SettingType.noteSharingAudience,
        value: audience,
        success: true,
      })
      trackEvent?.(EventName.MeetingNoteSharingChanged, { audience })
    } catch (error) {
      setOptimisticSettings(previousSettings)
      updateToast(error as string, 'error')
    } finally {
      setIsSavingSharingAudience(false)
    }
  }

  const handleNoteSharingSelect = (audience: NoteSharingAudience) => {
    if (isSavingNoteSharing || audience === noteSharingAudience) return
    // Only the full block confirms; the rest save immediately.
    if (audience === NoteSharingAudience.Owner) {
      setPendingSharingBlock(true)
      return
    }
    void applyNoteSharingAudience(audience)
  }

  const noteSharingContent = resolveNoteSharingContent(optimisticSettings)

  const handleNoteSharingContentSelect = async (content: NoteSharingContent) => {
    if (!enterprise || isSavingNoteSharing || content === noteSharingContent) return

    const previousSettings = optimisticSettings
    setIsSavingSharingContent(true)
    // Functional, like the rollback below: a concurrent save must not be clobbered by a
    // snapshot captured before it landed.
    setOptimisticSettings((current) => ({ ...current, [SettingType.noteSharingContent]: content }))

    try {
      await dispatch(
        updateEnterpriseSettingsThunk({
          settings: { [SettingType.noteSharingContent]: content },
        })
      ).unwrap()

      trackEvent?.(EventName.SettingChanged, {
        setting: SettingType.noteSharingContent,
        value: content,
        success: true,
      })
    } catch (error) {
      // Restore this key alone. Replacing the whole snapshot would drop any other setting the
      // admin optimistically changed while this request was in flight.
      setOptimisticSettings((current) => ({
        ...current,
        [SettingType.noteSharingContent]: previousSettings[SettingType.noteSharingContent],
      }))
      updateToast(error as string, 'error')
    } finally {
      setIsSavingSharingContent(false)
    }
  }

  const orgInsightsMode = resolveOrgInsightsMode(optimisticSettings)

  const handleOrgInsightsSelect = async (mode: OrgInsightsMode) => {
    if (isSavingOrgInsights || mode === orgInsightsMode) return
    setIsSavingOrgInsights(true)
    try {
      await applySettingsPatch({ [SettingType.orgInsightsMode]: mode })
    } finally {
      setIsSavingOrgInsights(false)
    }
  }

  const defaultSeatType = optimisticSettings[SettingType.defaultSeatType]
  const mobileSignInBlockAvailable = enterprise?.mobileSignInBlockAvailable === true

  const handleDefaultSeatTypeSelect = async (seatType: ProductSeatType) => {
    if (!seatSelectionEnabled || isSavingDefaultSeatType || seatType === defaultSeatType) return
    setIsSavingDefaultSeatType(true)
    try {
      await applySettingsPatch({ [SettingType.defaultSeatType]: seatType })
    } finally {
      setIsSavingDefaultSeatType(false)
    }
  }

  const handleSnippetSharingPolicySelect = async (selectedItems: DropdownItem[]) => {
    if (!enterprise || selectedItems.length === 0) return

    const previousSettings = optimisticSettings
    const previousSnippetSharingPolicyItems = snippetSharingPolicyItems
    const value = selectedItems[0].id as SnippetSharingPolicy
    const newSettings: EnterpriseSettings = {
      ...optimisticSettings,
      [SettingType.snippetSharingPolicy]: value,
    }
    setOptimisticSettings(newSettings)

    try {
      await dispatch(
        updateEnterpriseSettingsThunk({
          settings: { [SettingType.snippetSharingPolicy]: value },
        })
      ).unwrap()

      trackEvent?.(EventName.SettingChanged, { setting: SettingType.snippetSharingPolicy, value, success: true })
    } catch (error) {
      setOptimisticSettings(previousSettings)
      setSnippetSharingPolicyItems(previousSnippetSharingPolicyItems)
      updateToast(error as string, 'error')
    }
  }

  const handleCloudDataStorageSelect = async (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    await applySettingsPatch({ [SettingType.allowCloudSync]: selectedItems[0].id === CLOUD_SYNC_MANAGED_ID })
  }

  const handleScratchpadSelect = async (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    await toggleSettings(SettingType.allowScratchpad, selectedItems[0].id === SCRATCHPAD_ENABLED_ID)
  }

  const handleOrgFeatureSelect = async (feature: OrgFeatureCopy, selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    const enabled = selectedItems[0].id === FEATURE_ENABLED_ID
    if (enabled === (optimisticSettings[feature.key] !== false)) return
    await toggleSettings(feature.key, enabled)
  }

  const applyNotetakerRetention = async (value: NotetakerTranscriptRetention) => {
    if (!enterprise) return

    const previousSettings = optimisticSettings
    setOptimisticSettings({ ...optimisticSettings, [SettingType.notetakerTranscriptRetention]: value })
    setIsRetentionSaving(true)

    try {
      await dispatch(
        updateNotetakerRetentionThunk({ settings: { [SettingType.notetakerTranscriptRetention]: value } })
      ).unwrap()

      trackEvent?.(EventName.SettingChanged, {
        setting: SettingType.notetakerTranscriptRetention,
        value,
        success: true,
      })
    } catch (error) {
      setOptimisticSettings(previousSettings)
      updateToast(error as string, 'error')
    } finally {
      setIsRetentionSaving(false)
    }
  }

  const handleNotetakerRetentionSelect = async (selectedItems: DropdownItem[]) => {
    if (!enterprise || selectedItems.length === 0) return
    if (isRetentionSaving) return

    const value = selectedItems[0].id as NotetakerTranscriptRetention
    const current = getEffectiveNotetakerRetention(optimisticSettings)
    if (current === value) return

    if (retentionRequiresConfirm(current, value)) {
      setPendingRetention(value)
      return
    }
    await applyNotetakerRetention(value)
  }

  // Dedicated flag — independent of note sharing / retention.
  const showNotetakerControls = notetakerControlsFlagEnabled ?? false
  const showConsentConfirmationControl =
    (consentConfirmationFlagEnabled ?? false) ||
    enterprise?.settings?.[SettingType.notetakerConsentConfirmation] === true
  // Mirrors desktop's disclosure gate (`notetaker-consent-chat` only), independently of consent confirmation.
  const showConsentChatControls =
    consentChatFlagEnabled === true || enterprise?.settings?.[SettingType.notetakerConsentChatEnabled] === true
  // notetakerBusinessEarlyAccessAt is the server's durable success marker — flag ingestion lags behind it.
  const showNotetakerEarlyAccessHelper =
    notetakerBusinessAllowedFlag === false &&
    planType === PlanType.Enterprise &&
    hasLiveEnterpriseSubscription &&
    !optimisticSettings.notetakerBusinessEarlyAccessAt
  // Accepting the BAA requires org-wide Notetaker off; only gate when the toggle exists to comply.
  const handleAcceptBaaClick = () => {
    if (
      !notetakerHipaaCoexist &&
      !enterprise?.hipaaBaaSigned &&
      showNotetakerControls &&
      !optimisticSettings.notetakerDisabledAt
    ) {
      setShowBaaNotetakerGate(true)
      return
    }
    setIsViewingHipaa(true)
  }
  // `undefined` while loading resolves to hidden — no flash-in, same as note sharing above.
  const showSnippetSharingPolicyRow = snippetGovernanceFlagEnabled ?? false
  // Flag off still shows a stored deleting window, so an admin can change it.
  const storedRetention = enterprise?.settings?.[SettingType.notetakerTranscriptRetention]
  const showNotetakerRetentionRow =
    transcriptRetentionFlagEnabled === true || (storedRetention != null && isFiniteRetentionWindow(storedRetention))
  const showScratchpadRow = Boolean(enterprise) && planType === PlanType.Enterprise
  const hideEnterpriseUpsell = impersonating && Boolean(enterprise) && planType !== PlanType.Enterprise
  const hideSubscriptionUpsell = impersonating && Boolean(enterprise) && !hasActiveSubscription
  const showOrgFeatureRows =
    Boolean(enterprise) &&
    planType === PlanType.Enterprise &&
    optimisticSettings[SettingType.dictionarySnippetsKillSwitchUiVisible] !== false
  const snippetsOrgDisabled = optimisticSettings[SettingType.allowSnippets] === false
  const cloudSyncOff = getAllowCloudSync(optimisticSettings) === false
  const notetakerRetentionItems = getNotetakerRetentionDropdownItems(getEffectiveNotetakerRetention(optimisticSettings))
  const scratchpadItems = getScratchpadDropdownItems(optimisticSettings[SettingType.allowScratchpad] !== false)
  const notetakerRetentionEditable = planType === PlanType.Enterprise && isAdmin
  const showAlwaysStoreLocally = alwaysStoreLocallyFlagEnabled || localDataPolicy === LocalDataPolicy.AlwaysStore
  const pendingRetentionWindow = pendingRetention ? (RETENTION_WINDOW_LABEL[pendingRetention] ?? 'the new limit') : ''
  const snippetSharingDropdown = (
    <Dropdown
      items={snippetSharingPolicyItems}
      setItems={setSnippetSharingPolicyItems}
      onSelect={handleSnippetSharingPolicySelect}
      placeholder="Select policy"
      className={styles.localDataPolicyDropdown}
      disabled={snippetsOrgDisabled || !isAdmin}
    />
  )

  return (
    <div className={styles.settingList}>
      {!HIDE_LOGO_SETTING && (
        <div className={styles.settingSection}>
          <div className={styles.settingInfo}>
            <div className={styles.settingTitle}>Organization Logo</div>
            <div className={styles.settingDescription}>Upload your company logo (max 1MB)</div>
          </div>
          <LogoUpload logo={enterprise?.logo || null} name={enterprise?.name || ''} onUpload={handleLogoUpload} />
        </div>
      )}
      <div className={styles.settingSection}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Organization name</div>
          <div className={styles.settingDescription}>
            Human-friendly label for your organization, shown in user interfaces
          </div>
        </div>
        <PermissionTooltip>
          <Input
            value={name}
            onInput={setName}
            type="text"
            placeholder="Your company's name"
            className={styles.textbox}
            isDisabled={!isAdmin}
          />
        </PermissionTooltip>
      </div>

      <div className={styles.settingSection}>
        <DataControlsSection
          enterprise={enterprise}
          planType={planType}
          isAdmin={isAdmin}
          optimisticSettings={optimisticSettings}
          applySettingsPatch={applySettingsPatch}
        />
        {planType === PlanType.Enterprise && optimisticSettings[SettingType.ipAllowlistUiVisible] === true && (
          <IpAllowlistCard settings={optimisticSettings} isAdmin={isAdmin} />
        )}
        {planType === PlanType.Enterprise ? (
          <div className={`${styles.settingsItem} ${styles.alignTop}`} data-testid="org-hipaa-setting">
            <div className={styles.labelBlock}>
              <div className={styles.toggleLabel}>
                Org-wide HIPAA
                <Tooltip
                  text={notetakerHipaaCoexist ? HIPAA_WITH_NOTETAKER_TOOLTIP : HIPAA_TOOLTIP}
                  placement="top"
                  hideArrow
                >
                  <InfoIcon className={styles.infoIcon} />
                </Tooltip>
              </div>
              {!notetakerHipaaCoexist && <div className={styles.labelDescription}>Requires disabling Notetaker</div>}
            </div>
            {hipaaGatingEnabled && enterprise?.hipaaBaaSigned ? (
              <div className={styles.hipaaActions}>
                <PermissionTooltip>
                  <Button
                    title="View signed BAA"
                    onClick={() => setIsViewingHipaa(true)}
                    type="light"
                    isDisabled={!isAdmin}
                  />
                </PermissionTooltip>
                <PermissionTooltip>
                  <Button
                    title="Revoke BAA"
                    onClick={handleRevokeBaa}
                    type="dark"
                    isDisabled={!isAdmin}
                    isLoading={isRevoking}
                  />
                </PermissionTooltip>
              </div>
            ) : (
              <PermissionTooltip>
                <Button
                  title={enterprise?.hipaaBaaSigned ? 'View signed BAA' : 'View and accept BAA'}
                  onClick={handleAcceptBaaClick}
                  type="dark"
                  className={styles.controlButton}
                  isDisabled={!isAdmin}
                />
              </PermissionTooltip>
            )}
          </div>
        ) : hideEnterpriseUpsell ? null : (
          <div className={`${styles.settingsItem} ${styles.alignTop}`} data-testid="org-hipaa-setting">
            <div className={styles.labelBlock}>
              <div className={styles.toggleLabel}>Org-wide HIPAA</div>
              <div className={styles.labelDescription}>Available on Growth and Enterprise</div>
            </div>
            <PermissionTooltip enterpriseOnly={true}>
              <Button
                title="View and accept BAA"
                onClick={() => undefined}
                type="dark"
                className={styles.controlButton}
                isDisabled
              />
            </PermissionTooltip>
          </div>
        )}
        {enterprise?.hipaaBaaSigned && enterprise.hipaaBaaSignedBy && (
          <div className={styles.hipaaSignedNote}>
            BAA signed by {enterprise.hipaaBaaSignedBy}
            {enterprise.hipaaBaaSignedAt &&
              ` on ${new Date(enterprise.hipaaBaaSignedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
          </div>
        )}
        {!hideEnterpriseUpsell && (
          <div className={styles.settingsItem}>
            <div className={styles.toggleLabel}>SOC 2</div>
            {planType === PlanType.Enterprise ? (
              <a target="_blank" rel="noopener noreferrer" href="https://trust.wispr.ai" className={styles.emailLink}>
                <OpenLinkIcon />
              </a>
            ) : (
              <Tooltip text="Upgrade to Enterprise plan to use this feature" placement="top" hideArrow>
                <div className={`${styles.emailLink} ${styles.disabled}`}>
                  <OpenLinkIcon />
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div className={styles.settingSection}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Dictation</div>
          <div className={styles.settingDescription}>
            Dictation controls, including Dictionary and Snippets. These override individual member settings.
          </div>
        </div>
        {!(hideSubscriptionUpsell && hideEnterpriseUpsell) && (
          <div className={styles.settingGroupLabel}>Dictation history retention</div>
        )}
        {!hideSubscriptionUpsell && (
          <div className={`${styles.settingsItem} ${styles.indented}`} data-testid="cloud-data-storage-setting">
            <div className={styles.toggleLabel}>
              Cloud data storage
              <Tooltip text={CLOUD_DATA_STORAGE_TOOLTIP} placement="top" hideArrow>
                <InfoIcon className={styles.infoIcon} />
              </Tooltip>
            </div>
            <Tooltip
              text={
                !hasActiveSubscription
                  ? 'An active subscription is required to modify this setting'
                  : !isAdmin
                    ? 'Only admins can modify this'
                    : undefined
              }
              placement="top"
              hideArrow
            >
              <Dropdown
                items={getCloudDataStorageItems(getAllowCloudSync(optimisticSettings))}
                setItems={noopSetItems}
                onSelect={handleCloudDataStorageSelect}
                placeholder="Select policy"
                className={styles.localDataPolicyDropdown}
                disabled={!hasActiveSubscription || !isAdmin}
              />
            </Tooltip>
          </div>
        )}
        {!hideEnterpriseUpsell && (
          <div className={`${styles.settingsItem} ${styles.indented}`} data-testid="org-local-data-storage-setting">
            <div className={styles.toggleLabel}>
              Local data storage
              <Tooltip
                text={showAlwaysStoreLocally ? LOCAL_DATA_STORAGE_LOCKABLE_TOOLTIP : LOCAL_DATA_STORAGE_TOOLTIP}
                placement="top"
                hideArrow
              >
                <InfoIcon className={styles.infoIcon} />
              </Tooltip>
            </div>
            <PermissionTooltip enterpriseOnly={true}>
              <Dropdown
                items={getLocalDataPolicyItems(localDataPolicy, showAlwaysStoreLocally)}
                setItems={noopSetItems}
                onSelect={handleLocalDataStorageSelect}
                placeholder="Select data policy"
                className={styles.localDataPolicyDropdown}
                disabled={planType !== PlanType.Enterprise || !isAdmin}
              />
            </PermissionTooltip>
          </div>
        )}
        {!hideEnterpriseUpsell && (
          <div className={styles.settingsItem}>
            <div className={styles.toggleLabel}>
              Context awareness
              <Tooltip text={CONTEXT_AWARENESS_TOOLTIP} placement="top" hideArrow>
                <InfoIcon className={styles.infoIcon} />
              </Tooltip>
            </div>
            <PermissionTooltip enterpriseOnly={true}>
              <Dropdown
                items={contextAwarenessItems}
                setItems={setContextAwarenessItems}
                onSelect={handleContextAwarenessSelect}
                placeholder="Select policy"
                className={styles.localDataPolicyDropdown}
                disabled={planType !== PlanType.Enterprise || !isAdmin}
              />
            </PermissionTooltip>
          </div>
        )}
        {showOrgFeatureRows &&
          ORG_FEATURES.map((feature) => (
            <OrgFeatureRow
              key={feature.key}
              feature={feature}
              enabled={optimisticSettings[feature.key] !== false}
              isAdmin={isAdmin}
              onSelect={(selectedItems) => handleOrgFeatureSelect(feature, selectedItems)}
            />
          ))}
        {showSnippetSharingPolicyRow && (
          <div className={styles.settingsItem} data-testid="snippet-sharing-policy-setting">
            <div className={styles.toggleLabel}>Snippet sharing</div>
            {snippetsOrgDisabled ? (
              <Tooltip text={SNIPPET_SHARING_INERT_TOOLTIP} placement="top" hideArrow>
                {snippetSharingDropdown}
              </Tooltip>
            ) : (
              <PermissionTooltip>{snippetSharingDropdown}</PermissionTooltip>
            )}
          </div>
        )}
        {showAppDenySection && (
          <>
            <AppDenyListCard
              settings={optimisticSettings}
              isAdmin={isAdmin}
              authoringEnabled={appDenyAuthoringEnabled}
            />
            <BlockedUrlsCard
              settings={optimisticSettings}
              isAdmin={isAdmin}
              authoringEnabled={appDenyAuthoringEnabled}
            />
          </>
        )}
        <SettingItem
          config={SETTINGS_CONFIG[SettingType.allowTransforms]}
          optimisticSettings={optimisticSettings}
          toggleSettings={toggleSettings}
          isAdmin={isAdmin}
          defaultValue
          labelTooltip="Enable members to use generative AI to refine their text"
        />
      </div>

      <div className={styles.settingSection} data-testid="notetaker-settings-section">
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Notetaker</div>
          <div className={styles.settingDescription}>
            Organization-wide Notetaker controls, including Scratchpad. These override individual member settings.
          </div>
        </div>
        {showNotetakerControls && (
          <NotetakerControlsSection
            orgName={enterprise?.name || 'your organization'}
            isAdmin={isAdmin}
            allowNotetaker={!optimisticSettings.notetakerDisabledAt}
            hasEntitlementOverrides={Boolean(enterprise?.hasNotetakerEntitlementOverrides)}
            applySettingsPatch={applySettingsPatch}
            isSaving={isSaveLoading}
            enablingRevokesBaa={Boolean(hipaaGatingEnabled && enterprise?.hipaaBaaSigned && !notetakerHipaaCoexist)}
            isDisablingHipaaForNotetaker={isDisablingHipaaForNotetaker}
            onDisableHipaaAndEnableNotetaker={handleDisableHipaaForNotetaker}
            showEarlyAccessHelper={showNotetakerEarlyAccessHelper}
          />
        )}
        {showScratchpadRow && (
          <div className={styles.settingsItem} data-testid="scratchpad-allow-setting">
            <div className={styles.toggleLabel}>
              Scratchpad
              <Tooltip
                text={
                  <span id={scratchpadTooltipId} role="tooltip">
                    {SCRATCHPAD_TOOLTIP}
                  </span>
                }
                placement="top"
                hideArrow
                open={scratchpadTooltipOpen}
              >
                <span
                  className={styles.infoTooltipTrigger}
                  role="img"
                  tabIndex={0}
                  aria-label="About Scratchpad"
                  aria-describedby={scratchpadTooltipId}
                  onFocus={() => setScratchpadTooltipOpen(true)}
                  onBlur={() => setScratchpadTooltipOpen(false)}
                  onMouseEnter={() => setScratchpadTooltipOpen(true)}
                  onMouseLeave={() => setScratchpadTooltipOpen(false)}
                >
                  <InfoIcon className={styles.infoIcon} aria-hidden="true" />
                </span>
              </Tooltip>
            </div>
            <PermissionTooltip>
              <Dropdown
                items={scratchpadItems}
                setItems={noopSetItems}
                onSelect={handleScratchpadSelect}
                placeholder="Select policy"
                className={styles.dataControlDropdown}
                disabled={!isAdmin}
                ariaLabel="Scratchpad"
              />
            </PermissionTooltip>
          </div>
        )}
        {showNotetakerRetentionRow && (
          <div className={styles.settingsItem} data-testid="notetaker-retention-setting">
            <div className={styles.toggleLabel}>
              Transcript retention
              <Tooltip
                text={
                  cloudSyncOff
                    ? `${TRANSCRIPT_RETENTION_TOOLTIP} ${RETENTION_CLOUD_SYNC_OFF_TOOLTIP}`
                    : TRANSCRIPT_RETENTION_TOOLTIP
                }
                placement="top"
                hideArrow
              >
                <InfoIcon className={styles.infoIcon} />
              </Tooltip>
            </div>
            <PermissionTooltip enterpriseOnly={true}>
              <Dropdown
                items={notetakerRetentionItems}
                setItems={noopSetRetentionItems}
                onSelect={handleNotetakerRetentionSelect}
                placeholder="Select retention"
                className={styles.dataControlDropdown}
                disabled={!notetakerRetentionEditable}
                ariaLabel="Transcript retention"
              />
            </PermissionTooltip>
          </div>
        )}
        <NoteSharingAudienceSection
          audience={noteSharingAudience}
          isAdmin={isAdmin}
          isSaving={isSavingSharingAudience}
          siblingSaving={isSavingSharingContent}
          pendingBlock={pendingSharingBlock}
          onSelect={handleNoteSharingSelect}
          onConfirmBlock={() => {
            void applyNoteSharingAudience(NoteSharingAudience.Owner).finally(() => {
              setPendingSharingBlock(false)
            })
          }}
          onCancelBlock={() => setPendingSharingBlock(false)}
        />
        <NoteSharingContentSection
          content={noteSharingContent}
          isAdmin={isAdmin}
          isSaving={isSavingNoteSharing}
          sharingDisabled={noteSharingAudience === NoteSharingAudience.Owner}
          onSelect={(content) => void handleNoteSharingContentSelect(content)}
        />
        {orgInsightsFlagEnabled === true && (
          <OrgInsightsSection
            mode={orgInsightsMode}
            isAdmin={isAdmin}
            isSaving={isSavingOrgInsights}
            onSelect={(mode) => void handleOrgInsightsSelect(mode)}
          />
        )}
        {enterprise && mcpConnectorControlsFlagEnabled === true && (
          <ConnectorAccessSection
            settings={optimisticSettings}
            isAdmin={isAdmin}
            connectorAccess={connectorAccess}
            applySettingsPatch={applySettingsPatch}
          />
        )}
        {showNotetakerBlockedDomains && (
          <NotetakerBlockedDomainsCard
            settings={optimisticSettings}
            isAdmin={isAdmin}
            authoringEnabled={notetakerBlockedDomainsAuthoringEnabled}
          />
        )}
        {showConsentConfirmationControl && (
          <SettingItem
            config={SETTINGS_CONFIG[SettingType.notetakerConsentConfirmation]}
            optimisticSettings={optimisticSettings}
            labelTooltip={CONSENT_CONFIRMATION_TOOLTIP}
            toggleSettings={toggleSettings}
            isAdmin={isAdmin}
            hideEnterpriseTag
          />
        )}
        {showConsentChatControls && (
          <>
            <SettingItem
              config={SETTINGS_CONFIG[SettingType.notetakerConsentChatEnabled]}
              optimisticSettings={optimisticSettings}
              labelTooltip={CONSENT_CHAT_TOOLTIP}
              toggleSettings={toggleSettings}
              isAdmin={isAdmin}
              hideEnterpriseTag
            />
            {optimisticSettings[SettingType.notetakerConsentChatEnabled] === true && (
              <ConsentChatMessageSection
                message={optimisticSettings[SettingType.notetakerConsentChatMessage]}
                isAdmin={isAdmin}
                applySettingsPatch={applySettingsPatch}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.settingSection}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>User management</div>
          <div className={styles.settingDescription}>
            Control how users access and authenticate with your organization
          </div>
        </div>

        <AutoInviteDomainSettings
          enterprise={enterprise}
          optimisticSettings={optimisticSettings}
          toggleSettings={toggleSettings}
          isAdmin={isAdmin}
        />

        {seatSelectionEnabled && (
          <DefaultSeatTypeSection
            value={defaultSeatType}
            isAdmin={isAdmin}
            isSaving={isSavingDefaultSeatType}
            onSelect={(seatType) => void handleDefaultSeatTypeSelect(seatType)}
          />
        )}

        <SettingItem
          config={SETTINGS_CONFIG[SettingType.disableTeamTrial]}
          optimisticSettings={optimisticSettings}
          toggleSettings={toggleSettings}
          isAdmin={isAdmin}
        />

        <SettingItem
          config={SETTINGS_CONFIG[SettingType.hideLeaderboard]}
          optimisticSettings={optimisticSettings}
          toggleSettings={toggleSettings}
          isAdmin={isAdmin}
        />

        <SettingItem
          config={SETTINGS_CONFIG[SettingType.enforceMuteAudio]}
          optimisticSettings={optimisticSettings}
          toggleSettings={toggleSettings}
          isAdmin={isAdmin}
          labelTooltip="Mutes other audio on members' desktops while they dictate. Members can't turn this off."
        />

        {mobileSignInBlockAvailable && (
          <SettingItem
            config={SETTINGS_CONFIG[SettingType.disableMobileSignIn]}
            optimisticSettings={optimisticSettings}
            toggleSettings={toggleSettings}
            isAdmin={isAdmin}
            description="Members can't sign in to the Wispr Flow iOS or Android app. Members already signed in stay signed in."
          />
        )}

        {isAdmin && <SSOSection optimisticSettings={optimisticSettings} toggleSettings={toggleSettings} />}
      </div>

      <MdmRolloutSection
        providers={resolveMdmProviders(optimisticSettings)}
        updateIntent={resolveMdmUpdateIntent(optimisticSettings)}
        onSave={handleMdmDeclarationSave}
      />

      <DomainCaptureSection />

      {didChange && (
        <div className={styles.submitAction}>
          <Button title="Save changes" onClick={save} type="dark" isLoading={isSaveLoading} />
        </div>
      )}
      <HipaaBaa isOpen={isViewingHipaa} onClose={() => setIsViewingHipaa(false)} />
      <ConfirmDialog
        isOpen={showBaaNotetakerGate}
        title="Disable Notetaker first"
        message="Notetaker is temporarily incompatible with HIPAA while we adjust data agreements with our subprocessors. To enable org-wide HIPAA, first disable Notetaker in the Notetaker section."
        confirmLabel="Ok"
        confirmType="dark"
        hideCancel
        onConfirm={() => setShowBaaNotetakerGate(false)}
        onCancel={() => setShowBaaNotetakerGate(false)}
      />
      <ConfirmDialog
        isOpen={pendingToggle !== null}
        title={pendingToggle?.title ?? ''}
        message={pendingToggle?.message}
        warning={pendingToggle?.warning}
        confirmLabel={pendingToggle?.confirmLabel}
        isConfirming={isSaveLoading}
        onConfirm={async () => {
          if (!pendingToggle) return
          const { key, checked } = pendingToggle
          // Keep the dialog mounted while the request is in flight so the
          // spinner on the confirm button is visible. Dismiss after it resolves.
          try {
            await applyToggle(key, checked)
          } finally {
            setPendingToggle(null)
          }
        }}
        onCancel={() => setPendingToggle(null)}
      />
      <ConfirmDialog
        isOpen={pendingRetention !== null}
        title="Are you absolutely sure?"
        message={`This will delete transcripts older than ${pendingRetentionWindow} across your organization. This cannot be undone.`}
        confirmLabel="Yes, continue"
        confirmType="dark"
        isConfirming={isRetentionSaving}
        onConfirm={async () => {
          if (pendingRetention === null) return
          // Keep mounted during the request so the confirm spinner shows.
          try {
            await applyNotetakerRetention(pendingRetention)
          } finally {
            setPendingRetention(null)
          }
        }}
        onCancel={() => setPendingRetention(null)}
      />
      <ConfirmDialog
        isOpen={pendingLocalDataStorage !== null}
        title={pendingLocalDataStorage?.copy.title ?? ''}
        message={pendingLocalDataStorage?.copy.message}
        confirmLabel={pendingLocalDataStorage?.copy.confirmLabel}
        isConfirming={isSavingLocalDataStorage}
        onConfirm={async () => {
          if (pendingLocalDataStorage === null) return
          try {
            await applyLocalDataPolicy(pendingLocalDataStorage.policy)
          } finally {
            setPendingLocalDataStorage(null)
          }
        }}
        onCancel={() => setPendingLocalDataStorage(null)}
      />
    </div>
  )
}

export default EnterpriseSettingsPage
